import { createSlice } from "@reduxjs/toolkit";
import {
  copyEmployeeDetails,
  createPresignedPost,
  fetchEducationMaster,
  generatePresignUrl,
  getCandidateApprovalDetails,
  getCandidateApprovalSummary,
  getHrEditHistory,
  getJobDetailsDropdown,
  getPincodeDetails,
  hrReplaceDocument,
  markAsReviewedAndCreateId,
  saveOnboardingProgress,
  sendBackForCorrection,
} from "./thunk";

const initialState = {
  approvalSummaryResponse: null,
  approvalSummaryStatus: null,

  approvalDetailsResponse: null,
  approvalDetailsStatus: null,

  copyEmployeeDetailsResponse: null,
  copyEmployeeDetailsStatus: null,

  jobDetailsDropdownResponse: null,
  jobDetailsDropdownStatus: null,

  saveProgressResponse: null,
  saveProgressStatus: null,

  sendBackResponse: null,
  sendBackStatus: null,

  createIdResponse: null,
  createIdStatus: null,

  generatePresignUrlResponse: null,
  generatePresignUrlStatus: null,

  hrEditHistoryResponse: null,
  hrEditHistoryStatus: null,

  createPresignedPostResponse: null,
  createPresignedPostStatus: null,

  hrReplaceDocumentResponse: null,
  hrReplaceDocumentStatus: null,

  pincodeDetailsResponse: null,
  pincodeDetailsStatus: null,

  educationMasterResponse: null,
  educationMasterStatus: null,

  /** Status from get_candidate_approval_summary when opening Review Request */
  selectedReviewRequestStatus: null,
  selectedReviewOnboardingId: null,
};

const onboardingSlice = createSlice({
  name: "onboarding",
  initialState,
  reducers: {
    resetApprovalSummary: (state) => {
      state.approvalSummaryResponse = null;
      state.approvalSummaryStatus = null;
    },
    resetApprovalDetails: (state) => {
      state.approvalDetailsResponse = null;
      state.approvalDetailsStatus = null;
    },
    resetCopyEmployeeDetails: (state) => {
      state.copyEmployeeDetailsResponse = null;
      state.copyEmployeeDetailsStatus = null;
    },
    resetJobDetailsDropdown: (state) => {
      state.jobDetailsDropdownResponse = null;
      state.jobDetailsDropdownStatus = null;
    },
    resetSaveProgress: (state) => {
      state.saveProgressResponse = null;
      state.saveProgressStatus = null;
    },
    resetSendBack: (state) => {
      state.sendBackResponse = null;
      state.sendBackStatus = null;
    },
    resetCreateId: (state) => {
      state.createIdResponse = null;
      state.createIdStatus = null;
    },
    resetGeneratePresignUrl: (state) => {
      state.generatePresignUrlResponse = null;
      state.generatePresignUrlStatus = null;
    },
    resetHrEditHistory: (state) => {
      state.hrEditHistoryResponse = null;
      state.hrEditHistoryStatus = null;
    },
    resetCreatePresignedPost: (state) => {
      state.createPresignedPostResponse = null;
      state.createPresignedPostStatus = null;
    },
    resetHrReplaceDocument: (state) => {
      state.hrReplaceDocumentResponse = null;
      state.hrReplaceDocumentStatus = null;
    },
    resetPincodeDetails: (state) => {
      state.pincodeDetailsResponse = null;
      state.pincodeDetailsStatus = null;
    },
    resetEducationMaster: (state) => {
      state.educationMasterResponse = null;
      state.educationMasterStatus = null;
    },
    setSelectedReviewRequest: (state, action) => {
      state.selectedReviewRequestStatus = action.payload?.status ?? null;
      state.selectedReviewOnboardingId =
        action.payload?.onboardingId != null
          ? String(action.payload.onboardingId)
          : null;
    },
    clearSelectedReviewRequest: (state) => {
      state.selectedReviewRequestStatus = null;
      state.selectedReviewOnboardingId = null;
    },
    setInitialState: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(getCandidateApprovalSummary.pending, (state) => {
      state.approvalSummaryResponse = null;
      state.approvalSummaryStatus = "PENDING";
    });
    builder.addCase(getCandidateApprovalSummary.rejected, (state, action) => {
      state.approvalSummaryResponse = action.payload;
      state.approvalSummaryStatus = "ERROR";
    });
    builder.addCase(getCandidateApprovalSummary.fulfilled, (state, action) => {
      state.approvalSummaryResponse = action.payload;
      state.approvalSummaryStatus = "SUCCESS";
    });

    builder.addCase(getCandidateApprovalDetails.pending, (state) => {
      state.approvalDetailsResponse = null;
      state.approvalDetailsStatus = "PENDING";
    });
    builder.addCase(getCandidateApprovalDetails.rejected, (state, action) => {
      state.approvalDetailsResponse = action.payload;
      state.approvalDetailsStatus = "ERROR";
    });
    builder.addCase(getCandidateApprovalDetails.fulfilled, (state, action) => {
      state.approvalDetailsResponse = action.payload;
      state.approvalDetailsStatus = "SUCCESS";
    });

    builder.addCase(copyEmployeeDetails.pending, (state) => {
      state.copyEmployeeDetailsResponse = null;
      state.copyEmployeeDetailsStatus = "PENDING";
    });
    builder.addCase(copyEmployeeDetails.rejected, (state, action) => {
      state.copyEmployeeDetailsResponse = action.payload;
      state.copyEmployeeDetailsStatus = "ERROR";
    });
    builder.addCase(copyEmployeeDetails.fulfilled, (state, action) => {
      state.copyEmployeeDetailsResponse = action.payload;
      state.copyEmployeeDetailsStatus = "SUCCESS";
    });

    builder.addCase(getJobDetailsDropdown.pending, (state) => {
      // Keep any previous response while fetching to avoid dropdown flicker
      state.jobDetailsDropdownStatus = "PENDING";
    });
    builder.addCase(getJobDetailsDropdown.rejected, (state, action) => {
      state.jobDetailsDropdownResponse = action.payload;
      state.jobDetailsDropdownStatus = "ERROR";
    });
    builder.addCase(getJobDetailsDropdown.fulfilled, (state, action) => {
      state.jobDetailsDropdownResponse = action.payload;
      state.jobDetailsDropdownStatus = "SUCCESS";
    });

    builder.addCase(saveOnboardingProgress.pending, (state) => {
      state.saveProgressResponse = null;
      state.saveProgressStatus = "PENDING";
    });
    builder.addCase(saveOnboardingProgress.rejected, (state, action) => {
      state.saveProgressResponse = action.payload;
      state.saveProgressStatus = "ERROR";
    });
    builder.addCase(saveOnboardingProgress.fulfilled, (state, action) => {
      state.saveProgressResponse = action.payload;
      state.saveProgressStatus = "SUCCESS";
    });

    builder.addCase(sendBackForCorrection.pending, (state) => {
      state.sendBackResponse = null;
      state.sendBackStatus = "PENDING";
    });
    builder.addCase(sendBackForCorrection.rejected, (state, action) => {
      state.sendBackResponse = action.payload;
      state.sendBackStatus = "ERROR";
    });
    builder.addCase(sendBackForCorrection.fulfilled, (state, action) => {
      state.sendBackResponse = action.payload;
      state.sendBackStatus = "SUCCESS";
    });

    builder.addCase(markAsReviewedAndCreateId.pending, (state) => {
      state.createIdResponse = null;
      state.createIdStatus = "PENDING";
    });
    builder.addCase(markAsReviewedAndCreateId.rejected, (state, action) => {
      state.createIdResponse = action.payload;
      state.createIdStatus = "ERROR";
    });
    builder.addCase(markAsReviewedAndCreateId.fulfilled, (state, action) => {
      state.createIdResponse = action.payload;
      state.createIdStatus = "SUCCESS";
    });

    builder.addCase(generatePresignUrl.pending, (state) => {
      state.generatePresignUrlResponse = null;
      state.generatePresignUrlStatus = "PENDING";
    });
    builder.addCase(generatePresignUrl.rejected, (state, action) => {
      state.generatePresignUrlResponse = action.payload;
      state.generatePresignUrlStatus = "ERROR";
    });
    builder.addCase(generatePresignUrl.fulfilled, (state, action) => {
      state.generatePresignUrlResponse = action.payload;
      state.generatePresignUrlStatus = "SUCCESS";
    });

    builder.addCase(getHrEditHistory.pending, (state) => {
      state.hrEditHistoryResponse = null;
      state.hrEditHistoryStatus = "PENDING";
    });
    builder.addCase(getHrEditHistory.rejected, (state, action) => {
      state.hrEditHistoryResponse = action.payload;
      state.hrEditHistoryStatus = "ERROR";
    });
    builder.addCase(getHrEditHistory.fulfilled, (state, action) => {
      state.hrEditHistoryResponse = action.payload;
      state.hrEditHistoryStatus = "SUCCESS";
    });

    builder.addCase(createPresignedPost.pending, (state) => {
      state.createPresignedPostResponse = null;
      state.createPresignedPostStatus = "PENDING";
    });
    builder.addCase(createPresignedPost.rejected, (state, action) => {
      state.createPresignedPostResponse = action.payload;
      state.createPresignedPostStatus = "ERROR";
    });
    builder.addCase(createPresignedPost.fulfilled, (state, action) => {
      state.createPresignedPostResponse = action.payload;
      state.createPresignedPostStatus = "SUCCESS";
    });

    builder.addCase(hrReplaceDocument.pending, (state) => {
      state.hrReplaceDocumentResponse = null;
      state.hrReplaceDocumentStatus = "PENDING";
    });
    builder.addCase(hrReplaceDocument.rejected, (state, action) => {
      state.hrReplaceDocumentResponse = action.payload;
      state.hrReplaceDocumentStatus = "ERROR";
    });
    builder.addCase(hrReplaceDocument.fulfilled, (state, action) => {
      state.hrReplaceDocumentResponse = action.payload;
      state.hrReplaceDocumentStatus = "SUCCESS";
    });

    builder.addCase(getPincodeDetails.pending, (state) => {
      state.pincodeDetailsResponse = null;
      state.pincodeDetailsStatus = "PENDING";
    });
    builder.addCase(getPincodeDetails.rejected, (state, action) => {
      state.pincodeDetailsResponse = action.payload;
      state.pincodeDetailsStatus = "ERROR";
    });
    builder.addCase(getPincodeDetails.fulfilled, (state, action) => {
      state.pincodeDetailsResponse = action.payload;
      state.pincodeDetailsStatus = "SUCCESS";
    });

    builder.addCase(fetchEducationMaster.pending, (state) => {
      state.educationMasterStatus = "PENDING";
    });
    builder.addCase(fetchEducationMaster.rejected, (state, action) => {
      state.educationMasterResponse = action.payload;
      state.educationMasterStatus = "ERROR";
    });
    builder.addCase(fetchEducationMaster.fulfilled, (state, action) => {
      state.educationMasterResponse = action.payload;
      state.educationMasterStatus = "SUCCESS";
    });
  },
});

export const onboardingActions = onboardingSlice.actions;

export default onboardingSlice.reducer;
